import React from "react";
import { components } from "../../../../api/spec";
import PButton from "../../../ui/input/PButton";

type EventAttendeeSummarySchema =
  components["schemas"]["EventAttendeeSummarySchema"];

interface ReviewScreenProps {
  selectedAttendees: EventAttendeeSummarySchema[];
  message: string;
  onConfirm: () => void;
  onBack: () => void;
}

const ReviewScreen: React.FC<ReviewScreenProps> = ({
  selectedAttendees,
  message,
  onConfirm,
  onBack,
}) => {
  const getStatusLabel = (status: string): string => {
    switch (status) {
      case "not_started":
        return "Not started";
      case "started":
        return "Started";
      case "pending_review":
        return "Pending review";
      case "completed":
        return "Completed";
      case "not_signed_up":
        return "Not signed up";
      default:
        return "Unknown";
    }
  };

  return (
    <div className="flex flex-col h-full bg-black text-white text-left">
      {/* Header */}
      <div className="px-6 py-4 border-b border-white/20">
        <h2 className="text-lg font-semibold">Review & Send</h2>
      </div>

      {/* Content */}
      <div className="flex-1 px-6 py-4 space-y-6 overflow-y-auto">
        {/* Recipients Summary */}
        <div>
          <h3 className="text-sm font-medium mb-3">
            Recipients ({selectedAttendees.length} total)
          </h3>

          {/* Selected attendees list (collapsible for many) */}
          {selectedAttendees.length <= 10 ? (
            <div className="mt-4 space-y-2">
              <div className="space-y-1 grid grid-cols-2">
                {selectedAttendees.map((attendee) => (
                  <div
                    key={attendee.guid}
                    className="flex items-center text-sm "
                  >
                    <span className="mr-2">•</span>
                    <span>{attendee.full_name || "Unknown"}</span>
                    <span className="ml-2 text-white/80">
                      ({getStatusLabel(attendee.status || "unknown")})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <details className="mt-4">
              <summary className="cursor-pointer text-sm text-cyan-400 hover:text-cyan-500 font-medium">
                View all {selectedAttendees.length} recipients
              </summary>
              <div className="mt-2 max-h-48 overflow-y-auto space-y-1 pl-4 grid grid-cols-2">
                {selectedAttendees.map((attendee) => (
                  <div
                    key={attendee.guid}
                    className="flex items-center text-sm"
                  >
                    <span className="mr-2">•</span>
                    <span>{attendee.full_name || "Unknown"}</span>
                    <span className="ml-2 text-white/80">
                      ({getStatusLabel(attendee.status || "unknown")})
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* Message */}
        <div>
          <h3 className="text-sm font-medium mb-3">Message</h3>
          <div className="bg-blue-50 rounded-md p-4 text-sm text-blue-900 border border-blue-200 whitespace-pre-line">
            {message}
          </div>
          <p className="mt-2 text-xs text-white/80">
            Note: Placeholders like {"{name}"} will be replaced with actual
            values for each recipient
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-yellow-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Please confirm
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                This message will be sent immediately to all{" "}
                {selectedAttendees.length} selected recipients via SMS. This
                action cannot be undone.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-white/20">
        <div className="flex gap-3">
          <PButton type="button" onClick={onBack} className="w-full">
            Back
          </PButton>
          <PButton
            kind="success"
            type="button"
            onClick={onConfirm}
            className="w-full"
          >
            Send Message
          </PButton>
        </div>
      </div>
    </div>
  );
};

export default ReviewScreen;
